import React, { useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import PropTypes from "prop-types";
import { Redirect, Route, Switch, withRouter } from "react-router-dom";
import { I18n } from "react-polyglot";
import TagManager from "react-gtm-module";

import api from "../api";
import config from "../config";
import translations from "../translations";
import FormContextProvider from "../context/form-context";
import { getTimeoutDuration } from "../selectors/settings";
import { logoutAccount } from "../actions/logout";
import {
  HIDE_LOGOUT_WARNING_MODAL,
  SHOW_LOGOUT_WARNING_MODAL,
} from "../actions/ui";
import { getAllModulesWithProgress } from "../selectors/progress";
import { getModuleListError, getModuleListLoading } from "../selectors/core";
import useAutoLogout from "../hooks/useAutoLogout";

import PrivateRoute from "../components/PrivateRoute";
import HeaderContainer from "../containers/HeaderContainer";
import FooterContainer from "../containers/FooterContainer";
import LoginContainer from "../containers/LoginContainer";
import LoadingSpinner from "../components/LoadingSpinner";

import Videos from "./Videos/Videos";
import Video from "../components/edu/Video";
import {
  getVideos,
  getVideosError,
  getVideosLoading,
} from "../selectors/videos";

const ProfileContainer = React.lazy(() =>
  import("../containers/ProfileContainer")
);
const ModulesOverview = React.lazy(() =>
  import('../components/ModulesOverview')
);
const DashboardContainer = React.lazy(() =>
  import('../containers/dashboard/DashboardContainer')
);
const DiaryContainer = React.lazy(() => import('../containers/DiaryContainer'));
const DiarySetupContainer = React.lazy(() =>
  import('../containers/DiarySetupContainer')
);
const DiaryResetPinContainer = React.lazy(() =>
  import('../containers/DiaryResetPinContainer')
);

const ModuleDetailContainer = React.lazy(() =>
  import('../containers/ModuleDetailContainer')
);
const ModuleIntroPageContainer = React.lazy(() =>
  import('../containers/ModuleIntroPageContainer')
);
const LearnPageContainer = React.lazy(() =>
  import('./Lessons/LearnPageContainer')
);
const DischargePageContainer = React.lazy(() =>
  import('./Lessons/DischargePageContainer')
);
const QuestionViewContainer = React.lazy(() =>
  import('../containers/QuestionViewContainer')
);
const Onboarding = React.lazy(() => import('../components/Onboarding'));
const Glossary = React.lazy(() => import('../components/Glossary'));

const SubpageContainer = React.lazy(() =>
  import('../containers/SubpageContainer')
);

const ResetPasswordContainer = React.lazy(() =>
  import('./ResetPasswordContainer')
);
const DischargePathwayViewContainer = React.lazy(() =>
  import('./DischargePathwayViewContainer')
);
const AccountOptions = React.lazy(() =>
  import('../components/AccountOptions/AccountOptions')
);
const AnalyticsContainer = React.lazy(() =>
  import('../containers/AnalyticsContainer')
);
const TimedOutMessage = React.lazy(() =>
  import('../components/TimedOutMessage/TimedOutMessage')
);

function App(props) {
  const {
    history,
    loggedIn,
    settingsList,
    timeoutMinutes,
    modulesList,
    dischargeStepsList,
    modules,
    modulesLoading,
    modulesError,
    setUpAnalytics,
    analyticsId,
    fetchVideos,
    token,
    videos,
    videosLoading,
    videosError
  } = props;

  // get the settings so we know timeout duration
  useEffect(() => {
    if (!timeoutMinutes && loggedIn) {
      settingsList({});
    }
  }, [loggedIn, settingsList, timeoutMinutes]);

  useEffect(() => {
    if (modules.length === 0 && loggedIn && !modulesLoading && !modulesError) {
      modulesList({});
      dischargeStepsList({});
    }
  }, [loggedIn, modules.length, modulesError, modulesList, modulesLoading]);

  // Auto logout after duration
  useAutoLogout(loggedIn, token, history, timeoutMinutes);

  useEffect(() => {
    // listen for route changes...
    history.listen(() => {
      // remove the navigation dropdown body class
      // /src/components/NavigationDropdown/LearnNavigation.js
      document.body.classList.remove('navigation-dropdown--open');
    });
  }, [history]);

  useEffect(() => {
    props.requestsList({});
  }, [props.requestsList])

  useEffect(() => {
    // Google Tag Manager | Google Analytics
    const tagManagerArgs = {
      gtmId: 'GTM-M5398JV',
      auth: config.gtm.auth,
    };

    if (setUpAnalytics && analyticsId) {
      tagManagerArgs.dataLayer = {
        userId: analyticsId
      };
      TagManager.initialize(tagManagerArgs);
    }
  }, [analyticsId, setUpAnalytics]);

  useEffect(() => {
    if (!videos && loggedIn && !videosLoading && !videosError) {
      fetchVideos({});
    }
  }, [fetchVideos, videos, loggedIn]);

  return (
    <I18n
      key={props.locale}
      locale={props.locale}
      messages={translations[props.locale]}
    >
      <FormContextProvider>
        <div className="wrapper">
          {props.showHeader ? <HeaderContainer /> : null}

          <main
            className={`content-container ${
              props.showHeader ? '' : '--no-padding'
            }`}
            role="main"
          >
            <React.Suspense
              fallback={<LoadingSpinner size="large" colorScheme="blue" />}
            >
              <Switch>
                <Route
                  exact
                  path="/"
                  render={() => <Redirect to="/subpage/home" />}
                />
                <PrivateRoute exact path="/glossary" component={Glossary} />
                <PrivateRoute exact path="/videos" component={Videos} videos={videos} loading={videosLoading} error={videosError} />
                <PrivateRoute exact path="/videos/:videoSlug" component={Video} videos={videos} loading={videosLoading} />
                <Route path="/subpage/:page" component={SubpageContainer} />
                <Route path="/login" component={LoginContainer} />
                <Route
                  path="/password/reset/:resetPasswordToken"
                  component={ResetPasswordContainer}
                />
                <Route path="/timed-out" component={TimedOutMessage} />
                <Route
                  exact
                  path="/diary/reset/:resetPinToken"
                  component={DiaryResetPinContainer}
                />
                <PrivateRoute path="/profile" component={ProfileContainer} />
                <PrivateRoute
                  path="/dashboard"
                  component={DashboardContainer}
                  modules={modules}
                  loading={modulesLoading}
                  error={modulesError}
                />
                <PrivateRoute exact path="/diary" component={DiaryContainer} />
                <Route path="/onboarding" component={Onboarding} />
                <PrivateRoute
                  exact
                  path="/diary/setup"
                  component={DiarySetupContainer}
                />
                <PrivateRoute
                  path="/questions"
                  component={QuestionViewContainer}
                />
                <PrivateRoute
                  exact
                  path="/learn"
                  modules={modules}
                  loading={modulesLoading}
                  error={modulesError}
                  component={ModulesOverview}
                />
                <PrivateRoute
                  path="/learn/chapter/:moduleSlug/lessons/lesson/:lessonSlug/part/:partSlug"
                  component={LearnPageContainer}
                  modules={modules}
                />
                <PrivateRoute
                  path="/learn/chapter/:moduleSlug/pages/:pageSlug"
                  component={ModuleIntroPageContainer}
                />
                <PrivateRoute
                  exact
                  path="/learn/chapter/:moduleSlug"
                  component={ModuleDetailContainer}
                />
                <PrivateRoute
                  exact
                  path="/discharge"
                  component={DischargePathwayViewContainer}
                />
                <PrivateRoute
                  path="/discharge/:moduleSlug/lessons/:lessonSlug/part/:pageNumber"
                  component={DischargePageContainer}
                />
                <PrivateRoute
                  path="/options/:optionTab"
                  component={AccountOptions}
                />
                <PrivateRoute path="/options" component={AccountOptions} />
                <PrivateRoute path="/analytics" component={AnalyticsContainer} />
                <Route path="*" component={SubpageContainer} />
              </Switch>
            </React.Suspense>
          </main>
          {props.showFooter ? <FooterContainer /> : null}
        </div>
      </FormContextProvider>
    </I18n>
  );
}

App.propTypes = {
  locale: PropTypes.string.isRequired,
  history: PropTypes.shape({
    listen: PropTypes.func,
    push: PropTypes.func
  }).isRequired,
  showHeader: PropTypes.bool,
  showFooter: PropTypes.bool,
  loggedIn: PropTypes.bool.isRequired,
  setUpAnalytics: PropTypes.bool.isRequired,
  analyticsId: PropTypes.string,
  timeoutMinutes: PropTypes.string,
  settingsList: PropTypes.func,
  showWarningModal: PropTypes.func,
  hideWarningModal: PropTypes.func,
  token: PropTypes.string,
  dispatchLogout: PropTypes.func.isRequired
};

App.defaultProps = {
  showHeader: false,
  showFooter: false,
  timeoutMinutes: '',
  analyticsId: '',
  token: '',
  settingsList: () => {},
  showWarningModal: () => {},
  hideWarningModal: () => {}
};

function mapStateToProps(state) {
  return {
    locale: state.preferences.locale,
    showHeader: state.ui.showHeader,
    showFooter: state.ui.showFooter,
    loggedIn: state.auth.hasToken,
    token: state.auth.token,
    setUpAnalytics: state.analytics.setUpAnalytics,
    analyticsId: state.analytics.analyticsId,
    timeoutMinutes: getTimeoutDuration(state),
    modules: getAllModulesWithProgress(state),
    modulesLoading: getModuleListLoading(state),
    modulesError: getModuleListError(state),
    videos: getVideos(state),
    videosLoading: getVideosLoading(state),
    videosError: getVideosError(state)
  };
}

function mapDispatchToProps(dispatch) {
  const { settingsList, dischargeStepsList, dispatchLogout, modulesList, fetchVideos, requestsList } = bindActionCreators(
    {
      settingsList: api.actions.settingsList,
      dischargeStepsList: api.actions.dischargeSteps,
      dispatchLogout: logoutAccount,
      modulesList: api.actions.modulesList,
      fetchVideos: api.actions.videoList,
      requestsList: api.actions.requestsList
    },
    dispatch
  );
  return {
    settingsList,
    dischargeStepsList,
    dispatchLogout,
    modulesList,
    fetchVideos,
    requestsList,
    showWarningModal: () => dispatch({ type: SHOW_LOGOUT_WARNING_MODAL }),
    hideWarningModal: () => dispatch({ type: HIDE_LOGOUT_WARNING_MODAL })
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));



// WEBPACK FOOTER //
// ./src/containers/App.js